import { DocumentVersionStatus } from '@qualicloud/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { SgqUserPanel } from '@/components/sgq/sgq-user-panel';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  IconAlertOctagon,
  IconAlertTriangle,
  IconCheckCircle,
  IconClipboardCheck,
  IconClock,
  IconEye,
  IconFiles,
  IconInbox,
  IconMinusCircle,
  LoadingRegion,
  MetricCard,
  Select,
  Skeleton,
  SkeletonCard,
  cn,
} from '@/design-system';
import { documentTypesApi, documentsApi, queryKeys } from '@/lib/api';
import type { DashboardSummary, DocumentTypeNode, PendingTask } from '@/lib/api/types';
import { formatDateOnly, formatRelative } from '@/lib/format/datetime';
import { EXPIRING_SOON_DASHBOARD_DAYS } from '@/lib/validity-presentation';
import type { MasterListSearch } from '@/routes/search-schemas';
import { PageBody, PageHeader } from '@/shell/app-shell';
import { useAuth } from '@/providers/auth-provider';

/**
 * Painel.
 *
 * Os contadores reproduzem as SEIS chaves do `getDashboardSummary` do legado
 * (ged.md §14.5) — `aprovados`, `pendentes`, `reprovados`, `vencidos`,
 * `vencendo` e `total` — mais duas que o legado não tinha: aguardando minha
 * aprovação e aguardando minha ciência. Três correções:
 *
 *  · o `total` do legado era `aprovados + pendentes + reprovados + vencidos`,
 *    contando duas vezes um pendente vencido. Aqui é a contagem de documentos
 *    DISTINTOS, e a dica do cartão avisa que ele não é a soma dos demais;
 *  · o legado não aplicava o bypass administrativo nesta consulta (BUG-30), o
 *    que fazia os números do painel discordarem da Lista Mestra. Aqui o CORE
 *    usa a mesma resolução de ACL nos dois lugares;
 *  · o filtro por tipo (`$tipo_id`) existia no legado e voltou como combo — o
 *    parâmetro já estava no cliente e nenhuma tela o informava.
 *
 * Cada cartão é um BOTÃO que leva à Lista Mestra já filtrada — o número sozinho
 * não resolve nada; o usuário quer ver quais são. O filtro de tipo escolhido
 * aqui viaja junto na navegação.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  /**
   * Filtro por tipo — o `$tipo_id` do legado (ged.md §14.5), que alimentava o
   * combo `getDocTiposForDashboard()`. O parâmetro já existia no cliente e
   * nunca era informado, então o combo simplesmente não existia.
   */
  const [documentTypeId, setDocumentTypeId] = useState<string | undefined>(undefined);

  const typesQuery = useQuery({
    queryKey: queryKeys.documentTypes.tree(),
    queryFn: () => documentTypesApi.tree(),
    staleTime: 5 * 60_000,
  });

  const summaryQuery = useQuery({
    queryKey: queryKeys.dashboard.summary(documentTypeId),
    queryFn: () => documentsApi.dashboardSummary(documentTypeId),
  });

  const tasksQuery = useQuery({
    queryKey: queryKeys.dashboard.myTasks(),
    queryFn: () => documentsApi.myTasks(),
  });

  const summary = summaryQuery.data;
  const typeOptions = useMemo(() => flattenTypes(typesQuery.data ?? []), [typesQuery.data]);

  /** Repassa o tipo escolhido ao navegar para a Lista Mestra. */
  const listSearch = (extra: Partial<MasterListSearch>): MasterListSearch => ({
    ...(documentTypeId ? { documentTypeId } : {}),
    page: 1,
    ...extra,
  });

  return (
    <>
      <PageHeader
        title={`Bom trabalho, ${firstName(user?.displayName ?? '')}`}
        description="Situação dos documentos controlados e as pendências que dependem de você."
      />

      <PageBody className="flex flex-col gap-6">
        <section aria-labelledby="dashboard-counters-heading">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <h2 id="dashboard-counters-heading" className="text-base font-semibold text-content">
              Documentos controlados
            </h2>
            <div className="w-64">
              <Field label="Filtrar por tipo de documento">
                <Select
                  value={documentTypeId ?? '__all__'}
                  onValueChange={(value) =>
                    setDocumentTypeId(value === '__all__' ? undefined : value)
                  }
                  options={[
                    { value: '__all__', label: 'Todos os tipos' },
                    ...typeOptions.map((type) => ({ value: type.id, label: type.path })),
                  ]}
                />
              </Field>
            </div>
          </div>

          <LoadingRegion loading={summaryQuery.isPending} label="Carregando contadores…">
            {summaryQuery.isPending ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: 8 }, (_, index) => (
                  <SkeletonCard key={index} />
                ))}
              </div>
            ) : summary ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <MetricCard
                  label="Aprovados e vigentes"
                  value={summary.approved}
                  tone="success"
                  icon={<IconCheckCircle size={18} />}
                  hint="Versões aprovadas dentro do prazo de validade."
                  onClick={() =>
                    void navigate({
                      to: '/documentos',
                      search: listSearch({ status: [DocumentVersionStatus.APPROVED] }),
                    })
                  }
                />
                <MetricCard
                  label="Pendentes de aprovação"
                  value={summary.pending}
                  tone="warning"
                  icon={<IconClock size={18} />}
                  hint="Versões submetidas aguardando decisão."
                  onClick={() =>
                    void navigate({
                      to: '/documentos',
                      search: listSearch({ status: [DocumentVersionStatus.IN_REVIEW] }),
                    })
                  }
                />
                {/*
                  REPROVADOS — categoria do `getDashboardSummary` do legado
                  (ged.md §14.5) que o painel novo declarava no tipo e nunca
                  renderizava. Sem este cartão, uma versão reprovada não aparece
                  em nenhum contador e some da visão sinóptica.
                */}
                <MetricCard
                  label="Reprovados"
                  value={summary.rejected}
                  tone="danger"
                  icon={<IconMinusCircle size={18} />}
                  hint="Versões reprovadas por um aprovador. Exigem nova versão."
                  onClick={() =>
                    void navigate({
                      to: '/documentos',
                      search: listSearch({ status: [DocumentVersionStatus.REJECTED] }),
                    })
                  }
                />
                <MetricCard
                  label="Vencidos"
                  value={summary.expired}
                  tone="danger"
                  icon={<IconAlertOctagon size={18} />}
                  hint="Validade expirada. Emita uma nova versão."
                  onClick={() =>
                    void navigate({
                      to: '/documentos',
                      search: listSearch({ status: [DocumentVersionStatus.EXPIRED] }),
                    })
                  }
                />
                <MetricCard
                  label={`Vencendo em ${EXPIRING_SOON_DASHBOARD_DAYS} dias`}
                  value={summary.expiringSoon}
                  tone="warning"
                  icon={<IconAlertTriangle size={18} />}
                  hint="Documentos cuja validade expira nos próximos 30 dias."
                  onClick={() =>
                    void navigate({
                      to: '/documentos',
                      search: listSearch({ expiringWithinDays: EXPIRING_SOON_DASHBOARD_DAYS }),
                    })
                  }
                />
                <MetricCard
                  label="Aguardando minha aprovação"
                  value={summary.awaitingMyApproval}
                  tone="info"
                  icon={<IconClipboardCheck size={18} />}
                  hint="Versões em que você é aprovador designado."
                  onClick={() => void navigate({ to: '/aprovacoes' })}
                />
                <MetricCard
                  label="Aguardando minha ciência"
                  value={summary.awaitingMyAcknowledgement}
                  tone="info"
                  icon={<IconInbox size={18} />}
                  hint="Documentos distribuídos a você que exigem confirmação de leitura."
                  onClick={() =>
                    void navigate({
                      to: '/documentos',
                      search: listSearch({ onlyPendingMyAcknowledgement: true }),
                    })
                  }
                />
                {/*
                  TOTAL — o legado somava aprovados + pendentes + reprovados +
                  vencidos e contava duas vezes um pendente vencido (ged.md
                  §14.5). Aqui é a contagem de documentos DISTINTOS, e a dica diz
                  isso: o número não confere com a soma dos outros cartões de
                  propósito.
                */}
                <MetricCard
                  label="Total de documentos"
                  value={summary.total}
                  tone="neutral"
                  icon={<IconFiles size={18} />}
                  hint="Documentos distintos visíveis para você. Não é a soma dos cartões acima — um documento aparece em mais de uma categoria."
                  onClick={() => void navigate({ to: '/documentos', search: listSearch({}) })}
                />
              </div>
            ) : (
              <EmptyState
                title="Contadores indisponíveis"
                description="Não foi possível carregar os contadores agora. Recarregue a página em alguns instantes."
                action={
                  <Button variant="secondary" onClick={() => void summaryQuery.refetch()}>
                    Tentar novamente
                  </Button>
                }
              />
            )}
          </LoadingRegion>
        </section>

        <div className="grid gap-6 xl:grid-cols-[1fr_1.2fr]">
          <DocumentsByTypeChart summary={summary} loading={summaryQuery.isPending} />
          <PendingTasksList
            tasks={tasksQuery.data ?? []}
            loading={tasksQuery.isPending}
          />
        </div>

        {/*
          SGQ — Onda 2.
          O painel do GED responde "como estão os documentos". Este responde "o
          que trava por minha causa no ciclo da qualidade": NC sob minha
          responsabilidade, ação vencendo, eficácia que só eu posso verificar,
          reunião da semana e circular sem ciência. São cinco pendências que, no
          sistema legado, só apareciam se o usuário fosse procurar módulo a
          módulo.
        */}
        <SgqUserPanel />
      </PageBody>
    </>
  );
}

/**
 * Documentos por tipo.
 *
 * Barras horizontais em HTML puro, sem biblioteca de gráfico. Motivo: um
 * `<canvas>` é opaco para tecnologia assistiva e some na impressão em preto e
 * branco. Aqui a mesma informação é uma LISTA DE DEFINIÇÕES — cada item tem
 * rótulo, valor numérico e proporção em texto — e a barra é só o reforço
 * visual, marcada `aria-hidden`.
 */
function DocumentsByTypeChart({
  summary,
  loading,
}: {
  readonly summary: DashboardSummary | undefined;
  readonly loading: boolean;
}) {
  const data = summary?.byType ?? [];
  const max = data.reduce((acc, item) => Math.max(acc, item.total), 0);
  const totalDocuments = data.reduce((acc, item) => acc + item.total, 0);

  return (
    <Card>
      <CardHeader
        title="Documentos por tipo"
        description="Distribuição dos documentos controlados entre os tipos cadastrados."
      />

      <div className="mt-4">
        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-7 w-full" />
            ))}
          </div>
        ) : data.length === 0 ? (
          <EmptyState
            variant="compact"
            icon={<IconFiles size={20} />}
            title="Nenhum documento cadastrado"
            description="Assim que o primeiro documento for criado, a distribuição por tipo aparecerá aqui."
          />
        ) : (
          <dl className="flex flex-col gap-2.5">
            {data.map((item) => {
              const percent = totalDocuments === 0 ? 0 : (item.total / totalDocuments) * 100;
              return (
                <div key={item.documentTypeId}>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="min-w-0 truncate text-sm text-content">{item.documentType}</dt>
                    <dd className="shrink-0 text-sm font-medium text-content qc-tabular">
                      {item.total}
                      <span className="ml-1 text-xs font-normal text-content-subtle">
                        ({percent.toFixed(0).replace('.', ',')}%)
                      </span>
                    </dd>
                  </div>
                  <div
                    aria-hidden="true"
                    className="mt-1 h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
                  >
                    <div
                      className="h-full rounded-full bg-brand"
                      style={{ width: `${max === 0 ? 0 : (item.total / max) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </dl>
        )}
      </div>
    </Card>
  );
}

function PendingTasksList({
  tasks,
  loading,
}: {
  readonly tasks: readonly PendingTask[];
  readonly loading: boolean;
}) {
  return (
    <Card padding="none">
      <div className="p-4">
        <CardHeader
          title="Minhas pendências"
          description="Aprovações e ciências que dependem exclusivamente de você."
          action={
            tasks.length > 0 ? (
              <Button asChild variant="secondary" size="sm">
                <Link to="/aprovacoes">Ver fila completa</Link>
              </Button>
            ) : undefined
          }
        />
      </div>

      {loading ? (
        <div className="flex flex-col gap-2 px-4 pb-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={<IconCheckCircle size={20} />}
          title="Nenhuma pendência"
          description="Você não tem aprovações nem ciências aguardando. Novas pendências aparecem aqui e também na sua caixa de notificações."
        />
      ) : (
        <ul className="border-t border-border">
          {tasks.map((task) => (
            <li
              key={`${task.kind}-${task.versionId}`}
              className="border-b border-border-subtle last:border-b-0"
            >
              <Link
                to="/documentos/$documentId"
                params={{ documentId: task.documentId }}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-sunken',
                  'focus-visible:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                    task.kind === 'APPROVAL'
                      ? 'bg-warning-subtle text-warning-content'
                      : 'bg-info-subtle text-info-content',
                  )}
                >
                  {task.kind === 'APPROVAL' ? (
                    <IconClipboardCheck size={16} />
                  ) : (
                    <IconEye size={16} />
                  )}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-content">
                    {task.documentCode ? `${task.documentCode} — ` : ''}
                    {task.documentName}
                  </span>
                  <span className="block truncate text-xs text-content-subtle">
                    {task.documentType} · versão {task.version} · solicitado{' '}
                    {formatRelative(task.requestedAt)}
                  </span>
                </span>

                <Badge
                  tone={task.kind === 'APPROVAL' ? 'warning' : 'info'}
                  srPrefix="Tipo de pendência:"
                  className="shrink-0"
                >
                  {task.kind === 'APPROVAL' ? 'Aprovar' : 'Dar ciência'}
                </Badge>

                {task.dueAt ? (
                  <span className="hidden shrink-0 text-xs text-content-subtle qc-tabular sm:inline">
                    até {formatDateOnly(task.dueAt)}
                  </span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? 'usuário';
}

/** Achata a árvore de tipos preservando o rótulo hierárquico "Pai > Filho". */
function flattenTypes(nodes: readonly DocumentTypeNode[]): Array<{ id: string; path: string }> {
  const result: Array<{ id: string; path: string }> = [];
  const walk = (list: readonly DocumentTypeNode[]) => {
    for (const node of list) {
      if (node.active) result.push({ id: node.id, path: node.path || node.name });
      if (node.children.length > 0) walk(node.children);
    }
  };
  walk(nodes);
  return result;
}
